import { Component, EventEmitter, Inject, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, takeUntil } from 'rxjs';
import { MyConfigOrganization } from '../../../../../models/myConfig';
import { BackOfficeService } from '../../../../../services/backoffice.service';

@Component({
  selector: 'app-parking-pricing-bulk-update-form',
  standalone: false,
  templateUrl: './parking-pricing-bulk-update-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './parking-pricing-bulk-update-form.component.css'
})
export class ParkingPricingBulkUpdateFormComponent {
  @Output() saveChangesEvent = new EventEmitter<void>();
  isLoading: boolean = false;
  bulkUpdateParkingPricingForm: UntypedFormGroup;
  componentDestroyed$: Subject<boolean> = new Subject();
  activeRequests: number = 0;
  minDay: number = 0;
  maxDay: number = 0;

  constructor(
    private fb: UntypedFormBuilder,
    private BackOfficeService: BackOfficeService,
    public snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: {
      parkingId: any,
      parkingPricing: any[],
      organization: MyConfigOrganization,
    },
    @Inject(MatDialogRef<ParkingPricingBulkUpdateFormComponent>) private dialogRef: MatDialogRef<ParkingPricingBulkUpdateFormComponent>
  ) {
    this.initEmptyPricingForm();
  }

  ngOnInit(): void {
    this.data.parkingPricing ? this.initPricingForm() : this.initEmptyPricingForm();
    this.calculateMinMaxDays();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.calculateMinMaxDays();
  }

  calculateMinMaxDays() {
    this.data.parkingPricing.forEach((pricing) => {
      let dayValue = parseInt(pricing.code.split(':')[1], 10);
      if (!isNaN(dayValue)) {
        ++dayValue;
        this.minDay = this.minDay === 0 ? dayValue : Math.min(this.minDay, dayValue);
        this.maxDay = Math.max(this.maxDay, dayValue);
      }
    });

    if (this.bulkUpdateParkingPricingForm) {
      this.updateRangeValidators();
    }
  }

  updateRangeValidators() {
    this.bulkUpdateParkingPricingForm.get('fromDay')?.setValidators([
      Validators.required,
      Validators.min(this.minDay),
      Validators.max(this.maxDay)
    ]);

    this.bulkUpdateParkingPricingForm.get('tillDay')?.setValidators([
      Validators.required,
      Validators.min(this.minDay),
      Validators.max(this.maxDay)
    ]);

    this.bulkUpdateParkingPricingForm.get('fromDay')?.updateValueAndValidity();
    this.bulkUpdateParkingPricingForm.get('tillDay')?.updateValueAndValidity();
  }

  validateDayRange() {
    const fromDay = this.bulkUpdateParkingPricingForm.get('fromDay')?.value;
    const tillDay = this.bulkUpdateParkingPricingForm.get('tillDay')?.value;

    if (fromDay && tillDay && fromDay > tillDay) {
      this.bulkUpdateParkingPricingForm.get('fromDay')?.setErrors({ rangeInvalid: true });
      return false;
    }

    return true;
  }

  setSwedishTimeZone(controlName: string, event: any) {
    const selectedDate = new Date(event.value);
    if (selectedDate) {
      const swedishOffset = -new Date().getTimezoneOffset();
      const swedishDate = new Date(selectedDate.getTime() + swedishOffset * 60 * 1000);
      this.bulkUpdateParkingPricingForm.get(controlName)?.setValue(swedishDate.toISOString().replace('Z', ''));
    } else {
      this.bulkUpdateParkingPricingForm.get(controlName)?.setValue(null);
    }
  }

  private setLoadingState(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  private incrementRequests() {
    this.activeRequests++;
    this.setLoadingState(true);
  }

  private decrementRequests() {
    if (this.activeRequests > 0)
      this.activeRequests--;
    if (this.activeRequests === 0) {
      this.setLoadingState(false);
    }
  }

  submitForm() {
    if (!this.bulkUpdateParkingPricingForm.valid || !this.validateDayRange()) {
      this.markFormGroupTouched(this.bulkUpdateParkingPricingForm);
      return;
    }

    this.incrementRequests();

    var model: any = this.createBulkParkingPricingPayload();
    console.log('model: ', model);

    this.BackOfficeService
      .bulkUpdateOrganizationParkingPrice(this.data.organization.organizationId, model)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (ParkingPrices) => {
          // this.snackBar.open('Parking price created: ' + ParkingPrice.id, 'Success', {
          //   verticalPosition: 'top',
          //   duration: 2000
          // });
          this.decrementRequests();
          this.saveChangesEvent.emit(ParkingPrices);
          this.dialogRef.close();
        },
        error: (err) => {
          this.snackBar.open('Failed to create parking price: ' + err.message, 'Error', {
            verticalPosition: 'top',
            duration: 2000
          });
          this.decrementRequests();
        }
      });
  }

  markFormGroupTouched(formGroup: UntypedFormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if ((control as UntypedFormGroup).controls) {
        this.markFormGroupTouched(control as UntypedFormGroup);
      }
    });
  }

  createBulkParkingPricingPayload() {
    return {
      parkingId: this.bulkUpdateParkingPricingForm.controls['parkingId'].value !== '' ? this.bulkUpdateParkingPricingForm.controls['parkingId'].value : null,
      priceDifference: this.bulkUpdateParkingPricingForm.controls['priceDifference'].value !== '' ? this.bulkUpdateParkingPricingForm.controls['priceDifference'].value : null,
      fromDay: this.bulkUpdateParkingPricingForm.controls['fromDay'].value !== '' ? this.bulkUpdateParkingPricingForm.controls['fromDay'].value : null,
      tillDay: this.bulkUpdateParkingPricingForm.controls['tillDay'].value !== '' ? this.bulkUpdateParkingPricingForm.controls['tillDay'].value : null,
      parkings: this.data.parkingPricing
      .filter((pricing) => {
        let dayValue = parseInt(pricing.code.split(':')[1], 10);
        ++dayValue;
        return !isNaN(dayValue) && (dayValue >= this.bulkUpdateParkingPricingForm.controls['fromDay'].value && dayValue <= this.bulkUpdateParkingPricingForm.controls['tillDay'].value);
      })
      .map((pricing) => ({
        id: pricing.id,
        code: pricing.code,
        price: pricing.price + this.bulkUpdateParkingPricingForm.controls['priceDifference'].value/(1 + (pricing.vat/100)),
        vat: pricing.vat,
      }))
    };
  }

  initPricingForm() {
    this.bulkUpdateParkingPricingForm = this.fb.group({
      parkingId: [{ value: this.data.parkingId || '', disabled: this.isLoading }],
      priceDifference: [{ value: '', disabled: this.isLoading }, [Validators.required]],
      fromDay: [{ value: this.minDay || '', disabled: this.isLoading }, [Validators.required]],
      tillDay: [{ value: this.maxDay || '', disabled: this.isLoading }, [Validators.required]]
    });
  }

  initEmptyPricingForm() {
    this.bulkUpdateParkingPricingForm = this.fb.group({
      parkingId: [{ value: '', disabled: this.isLoading }],
      priceDifference: [{ value: '', disabled: this.isLoading }, [Validators.required]],
      fromDay: [{ value: '', disabled: this.isLoading }, [Validators.required]],
      tillDay: [{ value: '', disabled: this.isLoading }, [Validators.required]]
    });
  }

  clearDate(event: Event, value: any) {
    event.stopPropagation();
    value.setValue('', { emitEvent: false });
  }

  onFromDayChange() {
    this.validateDayRange();
  }

  onTillDayChange() {
    this.validateDayRange();
  }
}
